// Maps API states and errors to the 15 honest variants, each with a reason
// that names the observed code. Next actions are view specific and stay with
// the caller; these helpers only decide "which state, and why".

import { isApiError } from '../api/errors';
import type {
  ArchiveCall,
  CoinSearchProviderState,
  ConnectorStatus,
  HealthDependencyState,
  ProviderRuntimeStatus,
  SourceHealthState,
} from '../api/types';
import { describeContract, resolveTokenIdentity } from '../identity/contract';
import type { HonestStateVariant } from './variants';

export interface HonestDescriptor {
  readonly variant: HonestStateVariant;
  readonly reason: string;
}

function describe(variant: HonestStateVariant, reason: string): HonestDescriptor {
  return { variant, reason };
}

// HTTP and client errors (api-surface 1.2 error catalogue).
export function stateOfError(error: unknown): HonestDescriptor {
  if (isApiError(error)) {
    if (error.status === 401) return describe('Authentication required', `The server answered 401 ${error.code}. Sign in to continue.`);
    if (error.status === 403) return describe('Permission denied', `The server answered 403 ${error.code}: ${error.message}`);
    if (error.status === 429) return describe('Rate limited', `The server answered 429 ${error.code}. Wait for the window to reset.`);
    if (error.status === 400) return describe('Provider unavailable', `The request was rejected (400 ${error.code}): ${error.message}`);
    if (error.isTimeout) return describe('Provider unavailable', 'The request timed out before the server answered.');
    if (error.isNetwork) return describe('Provider unavailable', 'The browser could not reach the workspace service.');
    if (error.status === 503) return describe('Provider unavailable', `The server answered 503 ${error.code}: ${error.message}`);
    return describe('Provider unavailable', `The server answered ${error.status} ${error.code}: ${error.message}`);
  }
  if (error instanceof Error) return describe('Provider unavailable', error.message);
  return describe('Provider unavailable', 'The request failed for an unrecorded reason.');
}

// Archive coverage.sourceHealth.state (api-surface 2.2).
export function stateOfSourceHealth(state: SourceHealthState, connectorCount: number): HonestDescriptor {
  switch (state) {
    case 'connected':
      return describe('Ready', `${connectorCount} source connector${connectorCount === 1 ? '' : 's'} connected.`);
    case 'degraded':
      return describe('Partial', 'At least one source connector is degraded; retained calls stay visible.');
    case 'stale':
      return describe('Stale', 'No source connector has a fresh heartbeat; calls shown are the last observed.');
    case 'unconfigured':
      return describe('Setup required', 'No source connector is configured for this workspace.');
    default:
      return describe('Partial', `Source health reported an unrecognised state: ${String(state)}.`);
  }
}

// Evidence freshness. Source health calls a connected import "connected"
// whether or not it produced anything this week, so a five-day-old Telegram
// import stamped Ready while the same footer printed "Evidence 14 Aug". The
// newest observed instant decides, and the reason names the age.
export const EVIDENCE_FRESH_WINDOW_MS = 6 * 60 * 60 * 1000;

export function evidenceIsStale(latestObservedAt: string | null | undefined, now: number): boolean {
  if (!latestObservedAt) return false;
  const observed = Date.parse(latestObservedAt);
  if (!Number.isFinite(observed)) return false;
  return now - observed > EVIDENCE_FRESH_WINDOW_MS;
}

/** "5 days" or "9 h", for the reason line beside a stale state. */
export function evidenceAge(latestObservedAt: string | null | undefined, now: number): string {
  const observed = latestObservedAt ? Date.parse(latestObservedAt) : Number.NaN;
  if (!Number.isFinite(observed)) return 'an unrecorded time';
  const hours = Math.max(0, Math.round((now - observed) / 3600e3));
  return hours >= 48 ? `${Math.round(hours / 24)} days` : `${hours} h`;
}

// Connector rows from GET /api/beta/source-connectors.
export function stateOfConnector(status: ConnectorStatus, lastErrorCode: string | null): HonestDescriptor {
  switch (status) {
    case 'connected':
      return describe('Ready', 'Connector connected.');
    case 'pending':
      return describe('Setup required', 'Connector installed but not yet confirmed.');
    case 'degraded':
      return describe('Stale', `Connector degraded${lastErrorCode ? ` (${lastErrorCode})` : ''}.`);
    case 'error':
      return describe('Provider unavailable', `Connector reported an error${lastErrorCode ? ` (${lastErrorCode})` : ''}.`);
    case 'revoked':
      return describe('Setup required', 'Connector was revoked; reconnect to resume.');
    default:
      return describe('Partial', `Connector reported an unrecognised status: ${String(status)}.`);
  }
}

// GET /api/beta/market/runtime and providerRuntime rows on the overview.
export function stateOfProviderRuntime(status: ProviderRuntimeStatus, provider: string, lastErrorCode: string | null): HonestDescriptor {
  switch (status) {
    case 'live':
      return describe('Ready', `${provider} runtime is live.`);
    case 'connecting':
      return describe('Loading', `${provider} runtime is connecting.`);
    case 'stale':
      return describe('Stale', `${provider} runtime heartbeat is stale.`);
    case 'rate_limited':
      return describe('Rate limited', `${provider} runtime is rate limited${lastErrorCode ? ` (${lastErrorCode})` : ''}.`);
    case 'failed':
      return describe('Provider unavailable', `${provider} runtime failed${lastErrorCode ? ` (${lastErrorCode})` : ''}.`);
    case 'disabled':
      return describe('Setup required', `${provider} runtime is disabled on this host.`);
    default:
      return describe('Partial', `${provider} runtime reported an unrecognised status: ${String(status)}.`);
  }
}

// /health/ready dependency values: "ready", "disabled", or a not-ready string.
export function stateOfHealthDependency(name: string, value: HealthDependencyState): HonestDescriptor {
  if (value === 'ready') return describe('Ready', `${name} reports ready.`);
  if (value === 'disabled') return describe('Setup required', `${name} is disabled on this host.`);
  return describe('Provider unavailable', `${name} reports ${value}.`);
}

// coin-search providerState (api-surface 2.1).
export function stateOfCoinSearchProvider(state: CoinSearchProviderState, provider: string, candidateCount: number): HonestDescriptor {
  switch (state) {
    case 'connected':
      return candidateCount > 0
        ? describe('Ready', `${candidateCount} candidate${candidateCount === 1 ? '' : 's'} from ${provider}.`)
        : describe('No evidence observed', `${provider} answered with no candidates.`);
    case 'degraded':
      return describe('Partial', `${provider} answered without persisted candidates.`);
    case 'unavailable':
      return describe('Provider unavailable', `${provider} did not answer the search.`);
    default:
      return describe('Partial', `${provider} reported an unrecognised state: ${String(state)}.`);
  }
}

// research-cache ohlcv coverage state.
export function stateOfCandleCoverage(state: 'observed' | 'partial' | 'unavailable', absenceReason: string | null, returnedBuckets: number, gapCount: number): HonestDescriptor {
  switch (state) {
    case 'observed':
      return describe('Ready', `${returnedBuckets} persisted candles, no recorded gaps.`);
    case 'partial':
      return describe('Partial', `${returnedBuckets} persisted candles with ${gapCount} recorded gap${gapCount === 1 ? '' : 's'}.`);
    case 'unavailable':
      return describe('No evidence observed', absenceReason ? `No persisted candles: ${absenceReason}.` : 'No persisted candles for this token.');
    default:
      return describe('Partial', `Candle coverage reported an unrecognised state: ${String(state)}.`);
  }
}

// Identity on an archive row (CONTRACT.md "Token identity on rows"): metadata
// present is Ready; a valid address without metadata is Metadata unavailable
// with the server's imageUnavailableReason; a lowercased Solana address or an
// unresolved contract is Address unverified.
export function stateOfArchiveIdentity(row: Pick<ArchiveCall, 'contractAddress' | 'contractResolution' | 'ticker' | 'coinName' | 'imageUnavailableReason' | 'identityProvider'>): HonestDescriptor {
  const identity = resolveTokenIdentity({
    contract: row.contractAddress,
    name: row.coinName,
    symbol: row.ticker,
    contractResolution: row.contractResolution,
  });
  if (row.contractResolution === 'unresolved') {
    return describe('Address unverified', 'The archive could not resolve this call to an exact contract.');
  }
  if (identity.addressState === 'unverified') {
    return describe('Address unverified', identity.contract.reason === 'solana_address_case_lost'
      ? 'The Solana address was recorded lowercased, so its case cannot be verified.'
      : 'The recorded address is not a valid contract shape.');
  }
  if (identity.metadataState === 'unavailable') {
    return describe('Metadata unavailable', row.imageUnavailableReason
      ? `Token name and ticker not observed (${row.imageUnavailableReason}).`
      : 'Token name and ticker not observed for this contract.');
  }
  return describe('Ready', `Identity from ${row.identityProvider ?? 'the archive'}.`);
}

// A bare address typed or pasted (Coin Checker header).
export function stateOfAddress(address: string): HonestDescriptor {
  const contract = describeContract(address);
  if (!contract.isValid) return describe('Address unverified', 'The input is not a Solana or EVM contract address.');
  if (contract.addressState === 'unverified') return describe('Address unverified', 'The Solana address is lowercased, so its case cannot be verified.');
  return describe('Ready', `Exact ${contract.chain === 'evm' ? 'EVM' : 'Solana'} contract.`);
}

// Forward call tracker abstention (api-surface 2.2, 2.10).
export function stateOfAbstention(abstentionState: string, complete: boolean): HonestDescriptor {
  if (complete) return describe('Ready', 'Fixed-horizon outcome persisted.');
  switch (abstentionState) {
    case 'entry_unavailable':
      return describe('No evidence observed', 'No entry price was captured within the 300 s tolerance, so no return is typed.');
    case 'historical_market_windows_missing':
      return describe('No evidence observed', 'Historical market windows are missing, so the outcome is abstained.');
    default:
      return describe('No evidence observed', `Outcome abstained: ${abstentionState}.`);
  }
}

export interface QuerySnapshot {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly hasData: boolean;
}

// Generic TanStack result to state: pending is Loading; an error with retained
// data is Stale (rows stay, labelled); an error without data maps by error;
// success falls through to the caller's own content state.
export function stateOfQuery(snapshot: QuerySnapshot): HonestDescriptor | null {
  if (snapshot.isPending && !snapshot.hasData) return describe('Loading', 'Waiting for the server to answer.');
  if (snapshot.isError && snapshot.hasData) {
    const failure = stateOfError(snapshot.error);
    return describe('Stale', `Showing the last successful response; the latest refresh failed: ${failure.reason}`);
  }
  if (snapshot.isError) return stateOfError(snapshot.error);
  return null;
}
